import { Code2, Sparkles } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';

import { CopyButton } from '@/_shared/components/CopyButton';
import { DataTable } from '@/_shared/components/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/_shared/components/ui/chart';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/_shared/components/ui/dialog';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { CHART_COLORS } from '@/_shared/lib/charts/colors';
import { detectSeriesColumns, isNumeric, prepareChartData, toNumber } from '@/_shared/lib/charts/data';
import type { ChartConfig, PanelData } from '@/_shared/lib/charts/types';

import type { DashboardPanel as PanelType } from '../types';
import { FunnelChart } from './FunnelChart';
import { MetricCard } from './MetricCard';

// ─── Props ───

interface Props {
  panel: PanelType;
  data: PanelData | null;
  loading?: boolean;
  error?: string | null;
  onAskAssistant?: () => void;
}

// ─── Helpers ───

function buildChartConfig(seriesColumns: string[]): ChartConfig {
  const config: ChartConfig = {};
  for (let i = 0; i < seriesColumns.length; i++) {
    const col = seriesColumns[i]!;
    config[col] = {
      label: col,
      color: CHART_COLORS[i % CHART_COLORS.length]!,
    };
  }
  return config;
}

// ─── Chart Renderers ───

function renderBarChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
): React.ReactElement {
  return (
    <ChartContainer config={config} className="aspect-video w-full">
      <BarChart data={chartData} accessibilityLayer>
        <CartesianGrid vertical={false} />
        <XAxis dataKey={labelColumn} tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        {seriesColumns.map((col) => (
          <Bar key={col} dataKey={col} fill={config[col]?.color ?? CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ChartContainer>
  );
}

function renderLineChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
): React.ReactElement {
  return (
    <ChartContainer config={config} className="aspect-video w-full">
      <LineChart data={chartData} accessibilityLayer>
        <CartesianGrid vertical={false} />
        <XAxis dataKey={labelColumn} tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        {seriesColumns.map((col) => (
          <Line
            key={col}
            type="monotone"
            dataKey={col}
            stroke={config[col]?.color ?? CHART_COLORS[0]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}

function renderAreaChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
): React.ReactElement {
  return (
    <ChartContainer config={config} className="aspect-video w-full">
      <AreaChart data={chartData} accessibilityLayer>
        <CartesianGrid vertical={false} />
        <XAxis dataKey={labelColumn} tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        {seriesColumns.map((col) => (
          <Area
            key={col}
            type="monotone"
            dataKey={col}
            stroke={config[col]?.color ?? CHART_COLORS[0]}
            fill={config[col]?.color ?? CHART_COLORS[0]}
            fillOpacity={0.2}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
}

function renderPieChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
): React.ReactElement {
  // For pie charts, use the first series column as the value
  const valueColumn = seriesColumns[0] ?? '';

  return (
    <ChartContainer config={config} className="aspect-video w-full">
      <PieChart accessibilityLayer>
        <ChartTooltip content={<ChartTooltipContent nameKey={labelColumn} />} />
        <Pie data={chartData} dataKey={valueColumn} nameKey={labelColumn} cx="50%" cy="50%" outerRadius="80%" label>
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <ChartLegend content={<ChartLegendContent nameKey={labelColumn} />} />
      </PieChart>
    </ChartContainer>
  );
}

// ─── Sub-renderers ───

function ChartPanel({ panel, data }: { panel: PanelType; data: PanelData }): React.ReactElement {
  const { labelColumn, seriesColumns } = detectSeriesColumns(data);

  if (seriesColumns.length === 0) {
    return <div className="py-4 text-center text-sm text-muted-foreground">No numeric data to chart</div>;
  }

  const config = buildChartConfig(seriesColumns);
  const chartData = prepareChartData(data, labelColumn, seriesColumns);
  const chartType = panel.chartType ?? 'bar';

  switch (chartType) {
    case 'line':
      return renderLineChart(chartData, labelColumn, seriesColumns, config);
    case 'area':
      return renderAreaChart(chartData, labelColumn, seriesColumns, config);
    case 'pie':
      return renderPieChart(chartData, labelColumn, seriesColumns, config);
    case 'bar':
    default:
      return renderBarChart(chartData, labelColumn, seriesColumns, config);
  }
}

function TablePanel({ data }: { data: PanelData }): React.ReactElement {
  // DataTable expects a JSON string and optional metadata
  const content = JSON.stringify(data.rows);
  const metadata = data.rowCount != null ? { totalRows: data.rowCount } : undefined;
  return <DataTable content={content} metadata={metadata} />;
}

function MetricPanel({ panel, data }: { panel: PanelType; data: PanelData }): React.ReactElement {
  const firstRow = data.rows[0];
  const columns = data.columns;

  // Extract the main metric value from the first numeric column
  let value: number | null = null;
  if (firstRow && columns.length > 0) {
    // Try the first column, then search for a numeric one
    for (const col of columns) {
      const raw = firstRow[col];
      if (isNumeric(raw)) {
        value = toNumber(raw);
        break;
      }
    }
  }

  return (
    <MetricCard
      title={panel.title}
      value={value}
      format={panel.metricConfig?.format}
      prefix={panel.metricConfig?.prefix}
      suffix={panel.metricConfig?.suffix}
    />
  );
}

function FunnelPanel({ panel, data }: { panel: PanelType; data: PanelData }): React.ReactElement {
  const stepCol = panel.funnelConfig?.stepColumn ?? data.columns[0] ?? 'step';
  const valueCol = panel.funnelConfig?.valueColumn ?? data.columns[1] ?? 'count';

  const steps = data.rows.map((row) => ({
    label: row[stepCol] != null ? String(row[stepCol] as string | number) : '',
    count: toNumber(row[valueCol]),
  }));

  return <FunnelChart title={panel.title} steps={steps} />;
}

// ─── Panel Actions (top-right toolbar) ───

function ViewSqlButton({ sql, title }: { sql: string; title: string }): React.ReactElement {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground/60 transition-colors hover:text-primary"
          aria-label={`View SQL for ${title}`}
        >
          <Code2 size={14} />
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="flex items-center justify-between gap-4 pr-6 text-sm font-medium">
            <span className="truncate">{title} — SQL</span>
            <CopyButton text={sql} />
          </DialogTitle>
        </DialogHeader>
        <pre className="min-h-0 flex-1 overflow-auto bg-muted/30 px-5 py-4 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap text-foreground">
          {sql}
        </pre>
      </DialogContent>
    </Dialog>
  );
}

function PanelActions({
  sql,
  title,
  onAskAssistant,
  className = '',
}: {
  /** Public dashboards omit SQL from the projection — when undefined, the
   * View SQL button is skipped so anonymous viewers can not read the query. */
  sql?: string;
  title: string;
  onAskAssistant?: () => void;
  className?: string;
}): React.ReactElement {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {sql !== undefined && <ViewSqlButton sql={sql} title={title} />}
      {onAskAssistant && (
        <button
          type="button"
          onClick={onAskAssistant}
          className="text-muted-foreground/60 transition-colors hover:text-primary"
          aria-label={`Ask assistant about ${title}`}
        >
          <Sparkles size={14} />
        </button>
      )}
    </div>
  );
}

// ─── Loading Skeleton ───

function LoadingSkeleton({ type }: { type: PanelType['type'] }): React.ReactElement {
  switch (type) {
    case 'metric':
      return (
        <>
          <Skeleton className="h-8 w-24" />
          <Skeleton className="mt-2 h-4 w-16" />
        </>
      );
    case 'table':
      return (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      );
    case 'funnel':
      return (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-8 w-1/2" />
        </div>
      );
    case 'chart':
    default:
      return <Skeleton className="aspect-video w-full" />;
  }
}

// ─── Main Component ───

export function DashboardPanel({
  panel,
  data,
  loading = false,
  error = null,
  onAskAssistant,
}: Props): React.ReactElement {
  // Metric and funnel panels have their own Card wrapper
  const usesOwnCard = panel.type === 'metric' || panel.type === 'funnel';

  if (usesOwnCard && !loading && !error && data) {
    let content: React.ReactElement;
    switch (panel.type) {
      case 'metric':
        content = <MetricPanel panel={panel} data={data} />;
        break;
      case 'funnel':
        content = <FunnelPanel panel={panel} data={data} />;
        break;
      default:
        content = <></>;
    }
    return (
      <div className="relative">
        {content}
        <PanelActions
          sql={panel.sql}
          title={panel.title}
          onAskAssistant={onAskAssistant}
          className="absolute top-3 right-4"
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">{panel.title}</CardTitle>
          {!loading && <PanelActions sql={panel.sql} title={panel.title} onAskAssistant={onAskAssistant} />}
        </div>
      </CardHeader>
      <CardContent>
        {loading && <LoadingSkeleton type={panel.type} />}
        {error && (
          <div className="flex items-start gap-2 py-2">
            <span className="flex-1 text-sm text-red-700">{error}</span>
            {onAskAssistant && (
              <button
                onClick={onAskAssistant}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
              >
                Fix <Sparkles size={12} />
              </button>
            )}
          </div>
        )}
        {!loading && !error && data && (
          <>
            {panel.type === 'chart' && <ChartPanel panel={panel} data={data} />}
            {panel.type === 'table' && <TablePanel data={data} />}
          </>
        )}
        {!loading && !error && !data && (
          <div className="py-4 text-center text-sm text-muted-foreground">No data available</div>
        )}
      </CardContent>
    </Card>
  );
}
